/**
 * Who may press the in-call green "interpreting" button, while the platform
 * call is live and no terp has joined yet: the client who placed a direct
 * call, and any client in a group call (Nataly, via Robert 2026-10-05).
 * Reading storage (not redux) matches how the in-call invite panel decides
 * role and call context. Platform calls are recognizable by the room names
 * the server mints for them: `p2p-` for a direct call (also after it grows
 * into a group) and `group-` for a call placed as a group; queue rooms carry
 * neither. The stored call's clientId is the server's calls.client_id (sent
 * on p2p_ringing/accepted/join_room); an interpreter is recorded on it when
 * one joins (recordP2PInterpreterOnCall), which canRequestP2PInterpreter
 * reads. The server refuses an originator who has left a group call
 * (NOT_IN_CALL), so the button needs no membership check of its own.
 */
import { getPersistentItem, getPersistentJson, setPersistentItem } from '../vrs-auth/storage';
/** Rooms the server mints for platform calls (ws/p2p-handlers.js generateFriendlyRoomName). */
const PLATFORM_CALL_ROOM = /^(p2p|group)-/;
/** A VRI session's room (the server's queue VRI rooms). */
const VRI_SESSION_ROOM = /^vri-/i;
export function isP2PCallOriginator() {
    if (getPersistentItem('vrs_user_role') !== 'client') {
        return false;
    }
    const call = getPersistentJson('vrs_active_call');
    const user = getPersistentJson('vrs_user_info');
    return Boolean(call?.clientId
        && call.clientId === user?.id
        && typeof call.roomName === 'string'
        && PLATFORM_CALL_ROOM.test(call.roomName));
}
/**
 * Whether the web toolbar's Request Interpreter button belongs in this room.
 *
 * Asking the queue from inside a call creates a new interpreted room and moves
 * the client into it (the server mints the room; it ignores the one sent). So
 * it is offered only where that is what the client wants:
 * - never in a platform call's room (p2p-/group-): only the person who placed
 *   the call brings an interpreter in, with the green button
 *   (canRequestP2PInterpreter); anyone else would be pulled out of the call
 *   (the answering callee was offered this in the 2026-09-25 incident);
 * - never in a call the stored record says is interpreted (a VRS/VRI call, or
 *   one with an interpreter on it) — this also holds after a reload, when the
 *   queue's own "match found" state is gone. The retired room pill applied the
 *   same stored-call rule.
 *
 * @param {string|undefined} roomName - The current conference room.
 * @returns {boolean}
 */
export function canRequestQueueInterpreterInRoom(roomName) {
    if (typeof roomName === 'string' && (roomName.startsWith('p2p-') || roomName.startsWith('group-'))) {
        return false;
    }
    const call = getPersistentJson('vrs_active_call');
    if (!call || !roomName || call.roomName !== roomName) {
        return true;
    }
    return !(call.callType === 'vrs' || call.callType === 'vri'
        || (typeof call.interpreterId === 'string' && call.interpreterId.trim())
        || (typeof call.interpreterName === 'string' && call.interpreterName.trim()));
}
/**
 * Whether this client is in a platform call (a direct or a group call). Anyone
 * in it may bring the interpreter in: any Deaf person in a group call (Nataly,
 * via Robert 2026-10-05), either person in a direct call (Robert, 2026-10-07).
 * The server checks that they are still in the call (NOT_IN_CALL).
 */
function isP2PCallMember() {
    if (getPersistentItem('vrs_user_role') !== 'client') {
        return false;
    }
    const call = getPersistentJson('vrs_active_call');
    return Boolean(call?.callId && typeof call.roomName === 'string' && PLATFORM_CALL_ROOM.test(call.roomName));
}
/**
 * Whether this client started the VRI session stored for this device (a
 * `vri-` room whose stored call names them as its client): after its
 * interpreter left, only they, the host, may ask for another into the same
 * room, and they stay billed (Robert, 2026-10-09;
 * vrs-server/ws/vri-session-hangup.js).
 */
function isVriSessionHost() {
    if (getPersistentItem('vrs_user_role') !== 'client') {
        return false;
    }
    const call = getPersistentJson('vrs_active_call');
    const user = getPersistentJson('vrs_user_info');
    return Boolean(call?.callId && call.clientId && call.clientId === user?.id
        && typeof call.roomName === 'string' && VRI_SESSION_ROOM.test(call.roomName));
}
/**
 * The green button's gate (web and native), until an interpreter is on the
 * call: the originator of a direct call, anyone in a group call, and the
 * client of a VRI session whose interpreter left. Whoever asks in a platform
 * call is the anchor: the interpreter is tied to them and they are billed.
 * The queue's Request Interpreter button stays hidden in platform rooms and
 * VRI sessions either way (canRequestQueueInterpreterInRoom).
 */
export function canRequestP2PInterpreter() {
    if (!isP2PCallOriginator() && !isP2PCallMember() && !isVriSessionHost()) {
        return false;
    }
    const call = getPersistentJson('vrs_active_call');
    return !call?.interpreterId && !call?.interpreterName;
}
/**
 * Record that an interpreter is on the stored call, so neither the web nor the
 * native button offers another (also after a reload), and who they are tied
 * to (the anchor, docs/interpreted-group-calls.md). No-op for another call.
 */
export function recordP2PInterpreterOnCall(callId, interpreterName, anchor = {}) {
    const call = getPersistentJson('vrs_active_call');
    if (!callId || !call || call.callId !== callId) {
        return;
    }
    setPersistentItem('vrs_active_call', JSON.stringify({
        ...call,
        interpreterName: interpreterName || call.interpreterName || 'Interpreter',
        ...(anchor.anchorId ? { anchorId: anchor.anchorId } : {}),
        ...(anchor.anchorName ? { anchorName: anchor.anchorName } : {})
    }));
}
/**
 * The interpreter left a call that started person-to-person
 * (p2p_interpreter_left): the call goes on, and the person who placed it may
 * ask for another, so the button offers one again. No-op for another call.
 */
export function recordP2PInterpreterLeft(callId) {
    const call = getPersistentJson('vrs_active_call');
    if (!callId || !call || call.callId !== callId) {
        return;
    }
    const { interpreterId: _id, interpreterName: _name, ...rest } = call;
    setPersistentItem('vrs_active_call', JSON.stringify(rest));
}
